import React, { useState } from "react";

import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";

import { Link } from "react-router-dom";

import GenericTable from "components/organisms/GenericTable";
import {
    ActionsCell,
    StatusDotSmall,
    StatusPill,
} from "components/organisms/GenericTable/GenericTable.styled";

import StandartButton from "../../components/atoms/StandartButton";
import { ButtonVariant } from "../../Constant/button";
import TrackerActionsMenu from "./TrackerActionsMenu";
import {
    ChipRow,
    EmptyCellText,
    MiniCountBadge,
    MiniIconChip,
    MiniWarningDot,
    NameLink,
} from "./TrackerTable.styled";

const summarizeItems = (items, typeKey, typeNameOf, typeIdOf, iconResolver) => {
    const groups = new Map();
    (items || []).forEach((item) => {
        const key = String(item?.[typeKey]);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
    });
    return Array.from(groups.entries()).map(([key, groupItems]) => {
        const first = groupItems[0];
        return {
            key,
            label: typeNameOf(first),
            icon: iconResolver(typeIdOf(first)),
            count: groupItems.length,
            hasFailure: groupItems.some((item) => item?.status === 0),
        };
    });
};

const TRACKERS_DEFAULT_PAGE_SIZE = 25;
const TRACKERS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const failingNames = (items) =>
    (items || [])
        .filter((item) => item?.status === 0)
        .map((item) => item?.name)
        .filter(Boolean);

const buildAttentionMessage = (
    failingSources,
    failingDestinations,
    sourceWarningTooltip,
    destinationWarningTooltip,
) => {
    const parts = [];
    if (failingSources.length) {
        parts.push(`${sourceWarningTooltip} (${failingSources.join(", ")})`);
    }
    if (failingDestinations.length) {
        parts.push(
            `${destinationWarningTooltip} (${failingDestinations.join(", ")})`,
        );
    }
    return parts.join(" ");
};

const renderChipGroup = (groups, tracker, onNavigateTracker, emptyLabel) => {
    if (!groups.length) {
        return <EmptyCellText>{emptyLabel}</EmptyCellText>;
    }
    return (
        <ChipRow>
            {groups.map((group) => (
                <Tooltip
                    key={group.key}
                    placement="top"
                    title={
                        group.count > 1
                            ? `${group.label} ×${group.count}`
                            : group.label || ""
                    }
                >
                    <MiniIconChip
                        onClick={(event) => {
                            event.stopPropagation();
                            onNavigateTracker(tracker?.id);
                        }}
                    >
                        {group.icon && <img src={group.icon} alt="" />}
                        {group.count > 1 && (
                            <MiniCountBadge>{group.count}</MiniCountBadge>
                        )}
                        {group.hasFailure && <MiniWarningDot />}
                    </MiniIconChip>
                </Tooltip>
            ))}
        </ChipRow>
    );
};

const TrackerTable = ({
    trackers,
    getSourceIcon,
    getDestinationIcon,
    onNavigateTracker,
    onDeleteTracker,
    language,
    sourcesTitle,
    destinationsTitle,
    trackerNameTitle,
    statusTitle,
    healthyLabel,
    attentionLabel,
    idleLabel,
    noSourcesLabel,
    noDestinationsLabel,
    viewLabel,
    emptyStateLabel,
    sourceWarningTooltip = "Since the Access Token is expired, you must reconnect the source.",
    destinationWarningTooltip = "Since the Access Token is expired, you must reconnect the destination.",
    enablePagination = true,
    columnToggle = true,
    tableName,
}) => {
    const [pageSize, setPageSize] = useState(TRACKERS_DEFAULT_PAGE_SIZE);
    const columns = [
        {
            key: "name",
            header: trackerNameTitle,
            label: trackerNameTitle ?? "Tracker Name",
            width: 2,
            render: (tracker) => (
                <NameLink
                    component={Link}
                    to={`/tracker/${tracker?.id}`}
                    onClick={(event) => event.stopPropagation()}
                    data-cy="tracker-row-name"
                >
                    {tracker?.tracker_name}
                </NameLink>
            ),
        },
        {
            key: "sources",
            header: sourcesTitle,
            label: sourcesTitle ?? "Sources",
            width: 1.5,
            render: (tracker) => {
                const sourceGroups = summarizeItems(
                    tracker?.sources,
                    "source_type_id",
                    (item) => item?.source_type?.name,
                    (item) => item?.source_type?.id,
                    getSourceIcon,
                );
                return renderChipGroup(
                    sourceGroups,
                    tracker,
                    onNavigateTracker,
                    noSourcesLabel ?? "None",
                );
            },
        },
        {
            key: "destinations",
            header: destinationsTitle,
            label: destinationsTitle ?? "Destinations",
            width: 1.5,
            render: (tracker) => {
                const destinationGroups = summarizeItems(
                    tracker?.destinations,
                    "destination_type_id",
                    (item) => item?.destination_type?.name,
                    (item) => item?.destination_type?.id,
                    getDestinationIcon,
                );
                return renderChipGroup(
                    destinationGroups,
                    tracker,
                    onNavigateTracker,
                    noDestinationsLabel ?? "None",
                );
            },
        },
        {
            key: "status",
            header: statusTitle,
            label: statusTitle ?? "Status",
            width: 1.2,
            render: (tracker) => {
                const failingSources = failingNames(tracker?.sources);
                const failingDestinations = failingNames(tracker?.destinations);
                const hasAnyFailure =
                    failingSources.length > 0 || failingDestinations.length > 0;
                const hasAnyItems =
                    (tracker?.sources?.length ?? 0) +
                        (tracker?.destinations?.length ?? 0) >
                    0;
                const status = hasAnyFailure
                    ? "attention"
                    : hasAnyItems
                      ? "healthy"
                      : "idle";
                const statusLabel =
                    status === "attention"
                        ? (attentionLabel ?? "Needs attention")
                        : status === "healthy"
                          ? (healthyLabel ?? "Healthy")
                          : (idleLabel ?? "No connections");

                const pill = (
                    <StatusPill $status={status}>
                        <StatusDotSmall />
                        {statusLabel}
                    </StatusPill>
                );

                if (status !== "attention") return pill;

                const attentionMessage = buildAttentionMessage(
                    failingSources,
                    failingDestinations,
                    sourceWarningTooltip,
                    destinationWarningTooltip,
                );

                return (
                    <Tooltip placement="top" title={attentionMessage}>
                        <span>{pill}</span>
                    </Tooltip>
                );
            },
        },
        {
            key: "actions",
            header: "",
            label: "Actions",
            align: "right",
            hideable: false,
            fixedWidth: 160,
            render: (tracker) => (
                <ActionsCell onClick={(event) => event.stopPropagation()}>
                    <Tooltip title={viewLabel ?? "View"}>
                        <span>
                            <StandartButton
                                iconButton
                                variant={ButtonVariant.BLACK_OUTLINED}
                                to={`/tracker/${tracker?.id}`}
                                data-cy="tracker-view"
                            >
                                <VisibilityIcon fontSize="small" />
                            </StandartButton>
                        </span>
                    </Tooltip>
                    <TrackerActionsMenu
                        settingsTo={`/tracker-setting/${tracker?.id}`}
                        reportsTo={`/tracker-reports/${tracker?.id}`}
                        onDelete={() => onDeleteTracker?.(tracker)}
                        language={language}
                    />
                </ActionsCell>
            ),
        },
    ];

    return (
        <GenericTable
            columns={columns}
            rows={trackers ?? []}
            getRowKey={(tracker) => tracker?.id}
            onRowClick={
                onNavigateTracker
                    ? (tracker) => onNavigateTracker(tracker?.id)
                    : undefined
            }
            emptyState={emptyStateLabel ?? "No trackers found."}
            pageSize={enablePagination ? pageSize : undefined}
            onPageSizeChange={enablePagination ? setPageSize : undefined}
            pageSizeOptions={
                enablePagination ? TRACKERS_PAGE_SIZE_OPTIONS : undefined
            }
            columnToggle={columnToggle}
            tableName={tableName}
        />
    );
};

export default TrackerTable;
